import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { notification } from "antd";
import { getEmailSwmsContent, getSwmsFormData, sendSwmsEmail, getAccountManagers } from "../../../../../actions/v2/JobSwmsActions";
import { Strings } from "../../../../../dataProvider/localize";
import { ADMIN_DETAILS } from "../../../../../dataProvider/constant";
import { getFirstName, getFormattedPhone, getStorage } from "../../../../../utils/common";
import SwmsSendClientForm from "./SwmsSendClientForm";

import styles from "./SwmsSendClientModal.module.css";

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" className={styles.closeIcon}>
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
  </svg>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" className={styles.headerIcon}>
    <path d="M536.4-26.3c9.8-3.5 20.6-1 28 6.3s9.8 18.2 6.3 28l-178 496.9c-5 13.9-18.1 23.1-32.8 23.1-14.2 0-27-8.6-32.3-21.7l-64.2-158c-4.5-11-2.5-23.6 5.2-32.6l94.5-112.4c5.1-6.1 4.7-15-.9-20.6s-14.6-6-20.6-.9L229.2 276.1c-9.1 7.6-21.6 9.6-32.6 5.2L38.1 216.8c-13.1-5.3-21.7-18.1-21.7-32.3 0-14.7 9.2-27.8 23.1-32.8l496.9-178z" />
  </svg>
);

const extractPdfLink = (payload, swms) => {
  const candidates = [
    payload && payload.pdfLink,
    payload && payload.pdf_link,
    payload && payload.swms_data && payload.swms_data.pdfLink,
    payload && payload.swms_data && payload.swms_data.pdf_link,
    payload && payload.swms_data && payload.swms_data.swms_pdf_link,
    payload && payload.document && payload.document.pdfLink,
    payload && payload.document && payload.document.pdf_link,
    swms && swms.raw && swms.raw.pdfLink,
    swms && swms.raw && swms.raw.pdf_link
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (Array.isArray(value) && value.length > 0) {
      const firstLink = value.find(Boolean);
      if (firstLink) {
        return firstLink;
      }
    }

    if (value) {
      return value;
    }
  }

  return "";
};

const getAdminDetails = () => {
  try {
    const parsed = JSON.parse(getStorage(ADMIN_DETAILS));
    return parsed || {};
  } catch (error) {
    return {};
  }
};

export default function SwmsSendClientModal({ swms, jobId, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const controller = useRef();

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [managers, setManagers] = useState([]);
  const [noReplyData, setNoReplyData] = useState("");
  const [pdfLink, setPdfLink] = useState("");

  const adminDetails = useMemo(() => getAdminDetails(), []);
  const orgId = adminDetails && adminDetails.organisation ? adminDetails.organisation.id : null;
  const formName = swms && swms.swms_form_name ? swms.swms_form_name : "SWMS";
  const formNumber = swms && swms.swms_number ? swms.swms_number : "-";

  const loadEmailData = () => {
    if (!swms || !swms.swms_form_id || !jobId) {
      setLoadingInitial(false);
      notification.error({
        message: Strings.error_title,
        description: Strings.generic_error,
        className: "ant-error"
      });
      return;
    }

    if (controller.current) {
      controller.current.abort();
    }

    controller.current = new AbortController();
    setLoadingInitial(true);

    const managerPromise = orgId ? dispatch(getAccountManagers(orgId)) : Promise.resolve([]);

    Promise.all([
      dispatch(getEmailSwmsContent({ signal: controller.current.signal })),
      dispatch(getSwmsFormData({ signal: controller.current.signal }, swms.swms_form_id, jobId)),
      managerPromise
    ]).then((res) => {
      const template = res[0] || {};
      const swmsResponse = res[1] || {};
      const accountManagers = Array.isArray(res[2]) ? res[2] : [];
      const swmsData = swmsResponse && swmsResponse.swms_data ? swmsResponse.swms_data : {};
      const clientDetails = swmsData && swmsData.client_details ? swmsData.client_details : {};
      const siteDetails = swmsData && swmsData.site_details ? swmsData.site_details : {};

      const resolvedPdfLink = extractPdfLink(swmsResponse, swms);
      setPdfLink(resolvedPdfLink);

      const orgName = adminDetails && adminDetails.organisation && adminDetails.organisation.name ? adminDetails.organisation.name : "";
      const siteName = siteDetails && siteDetails.site_name ? siteDetails.site_name : (swms && swms.site_name ? swms.site_name : "");
      const userName = adminDetails && adminDetails.name
        ? `${adminDetails.name} ${adminDetails.last_name ? adminDetails.last_name : ""}`.trim()
        : "";
      const userPhoneNumber = adminDetails && adminDetails.phone_number ? getFormattedPhone(adminDetails.phone_number) : "";
      const userEmailAddress = adminDetails && adminDetails.email_address
        ? `<a href=\"mailto:${adminDetails.email_address}\">${adminDetails.email_address}</a>`
        : "";
      const clientFirstName = clientDetails && clientDetails.client_contact_name
        ? getFirstName(clientDetails.client_contact_name)
        : "";

      let subject = template && template.subject ? `${template.subject}` : "SWMS for {{site_name}}";
      let body = template && template.body ? `${template.body}` : "";

      subject = subject.replace(/\{\{org_name\}\}/g, orgName);
      subject = subject.replace(/\{\{site_name\}\}/g, siteName);

      body = body.replace(/(?:\r\n|\r|\n)/g, "");
      body = body.replace(/\{\{client_person_first_name\}\}/g, clientFirstName);
      body = body.replace(/\{\{user_phone_number\}\}/g, userPhoneNumber);
      body = body.replace(/\{\{user_email\}\}/g, userEmailAddress);
      body = body.replace(/\{\{user_name\}\}/g, userName);
      body = body.replace(/\{\{site_name\}\}/g, siteName);

      const swmsLabel = formNumber && formNumber !== "-" ? `${formName} (${formNumber})` : formName;
      const linkMarkup = resolvedPdfLink ? `<ul><li><a href=\"${resolvedPdfLink}\">${swmsLabel}</a></li></ul>` : "<ul></ul>";
      body = body.replace(/\{\{link\}\}/g, linkMarkup);
      body = body.replace(/\{\{service_name\}\}/g, Strings.SERVICE_NAME);
      body = body.replace(/\{\{service_email\}\}/g, `<a href=\"mailto:${Strings.SERVICE_EMAIL}\">${Strings.SERVICE_EMAIL}</a>`);
      body = body.replace(/<p><\/p>/g, "<br />");

      let extractedNoReply = "";
      if (body.includes("<p>____no_reply</p>") && body.includes("<p>no_reply____</p>")) {
        const start = body.indexOf("<p>____no_reply</p>");
        const end = body.lastIndexOf("<p>no_reply____</p>");

        if (start !== -1 && end !== -1) {
          const signEnd = end + 15;
          const dataStart = start + 15;
          const noReply = body.substr(dataStart, (end - dataStart));
          extractedNoReply = "<!--[if mso]><table role=\"presentation\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\" width=\"600\"><tr><td><![endif]--><div style='width: auto;border:none;border-top:solid #000000 1.0pt; border-bottom: solid #000000 1.0pt; padding:4.0pt 0cm 4.0pt 0cm; margin: 20pt 0cm 10pt 0cm;'>"
            + noReply + "</div><!--[if mso]></td></tr></table><![endif]-->";
          body = body.substring(0, start) + body.substring(signEnd);
        }
      }

      setNoReplyData(extractedNoReply);

      const fromEmail = template && template.from_email ? template.from_email : Strings.SERVICE_EMAIL;
      const from = `${userName} (${orgName}) via ${Strings.SERVICE_NAME} <${fromEmail}>`;

      const to = clientDetails && clientDetails.client_contact_email
        ? [{ label: clientDetails.client_contact_email, value: clientDetails.client_contact_email }]
        : [];
      const cc = adminDetails && adminDetails.email_address
        ? [{ label: adminDetails.email_address, value: adminDetails.email_address }]
        : [];
      const bcc = Array.isArray(template && template.to_bcc)
        ? template.to_bcc.filter(Boolean).map((address) => ({ label: address, value: address }))
        : (template && template.to_bcc ? [{ label: template.to_bcc, value: template.to_bcc }] : []);

      setManagers(accountManagers
        .filter((manager) => manager && (manager.email || manager.email_address))
        .map((manager) => {
          const email = manager.email ? manager.email : manager.email_address;
          const name = manager.first_name ? manager.first_name : "Manager";
          return { label: `${name} <${email}>`, value: email };
        }));

      setInitialValues({
        from,
        to,
        cc,
        bcc,
        subject,
        body,
        attachment_docs: []
      });
      setLoadingInitial(false);
    }).catch((err) => {
      setLoadingInitial(false);
      notification.error({
        message: Strings.error_title,
        description: err || Strings.generic_error,
        className: "ant-error"
      });
    });
  };

  useEffect(() => {
    loadEmailData();

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    };
  }, []);

  const handleClose = () => {
    if (loadingInitial || submitting) {
      return;
    }

    onClose();
  };

  const handleSubmitEmail = (values) => {
    if (!pdfLink) {
      notification.error({
        message: Strings.error_title,
        description: "Active version PDF link is unavailable.",
        className: "ant-error"
      });
      return;
    }

    const toList = Array.isArray(values && values.to) ? values.to : [];
    if (toList.length === 0) {
      notification.error({
        message: Strings.error_title,
        description: "Please add at least one recipient in To.",
        className: "ant-error"
      });
      return;
    }

    setSubmitting(true);

    let content = values && values.body ? values.body : "";
    content = content + noReplyData;
    content = "<!--[if mso]><table role=\"presentation\" cellspacing=\"0\" cellpadding=\"0\" border=\"0\"><tr><td><![endif]--><div style=\"font-family: 'calibri', sans-serif !important; font-size: 11pt !important; width: fit-content;\">" + content + "</div><!--[if mso]></td></tr></table><![endif]-->";
    content = content.replace(/font-family:.?inherit;/g, "font-family: 'calibri', sans-serif;");
    content = content.replace(/<p><\/p>/g, "");
    content = content.replace(/(?:\r\n|\r|\n)/g, "");
    content = content.replace(/<ins>/g, "<u>");
    content = content.replace(/<\/ins>/g, "</u>");

    const ccList = Array.isArray(values && values.cc) ? values.cc : [];
    const bccList = Array.isArray(values && values.bcc) ? values.bcc : [];

    const formData = {
      to: toList.map((item) => item.value).join(","),
      from: values && values.from ? values.from : "",
      subject: values && values.subject ? values.subject : "",
      bcc: bccList.map((item) => item.value).join(","),
      cc: ccList.map((item) => item.value).join(","),
      body: content,
      file_path: "job-swms/email-attachments/",
      s3_attachements: []
    };

    let uploading = false;
    const attachments = values && Array.isArray(values.attachment_docs) ? values.attachment_docs : [];
    if (attachments.length > 0) {
      if (attachments.every((item) => item && item.file_name && item.file_name.isUploaded === true)) {
        formData.s3_attachements = attachments.map((item) => item.file_name.name);
      } else {
        uploading = true;
        notification.error({
          message: Strings.error_title,
          description: "An attachment is still uploading. Please wait for all uploads to finish.",
          className: "ant-error"
        });
      }
    }

    if (uploading) {
      setSubmitting(false);
      return;
    }

    dispatch(sendSwmsEmail(formData)).then((message) => {
      setSubmitting(false);
      notification.success({
        message: Strings.success_title,
        description: message || "Email Sent Successfully.",
        className: "ant-success"
      });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    }).catch((err) => {
      setSubmitting(false);
      notification.error({
        message: Strings.error_title,
        description: err || Strings.generic_error,
        className: "ant-error"
      });
    });
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerIconWrap}>
              <SendIcon />
            </span>
            <div className={styles.headerText}>
              <h3>Send SWMS to Client</h3>
              <p>{formName} · {formNumber}</p>
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={handleClose} disabled={loadingInitial || submitting} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className={styles.body}>
          {loadingInitial && (
            <div className={styles.loadingState}>
              <CircularProgress size={24} />
              <p>Preparing email details...</p>
            </div>
          )}

          {!loadingInitial && initialValues && (
            <SwmsSendClientForm
              initialValues={initialValues}
              onSubmitForm={handleSubmitEmail}
              onClose={handleClose}
              loading={loadingInitial}
              isSubmitting={submitting}
              managers={managers}
              pdfLinkMissing={!pdfLink}
            />
          )}
        </div>
      </div>
    </div>
  );
}
